import {
    useEffect,
    useState,
} from 'react';

import {
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
} from 'aws-amplify/auth';

import {
    getInitialFormData,
} from '../../domain/data-collection/defaults';

import {
    MATERIAL_DEFAULTS,
    deriveOperationType,
} from '../../domain/data-collection/constants';

import {
    validateForm,
} from '../../domain/data-collection/validation';

import {
  saveProfileDraft,
  parseDataCollectionCsv,
} from '../../domain/data-collection/csv';

import {
    showAppNotification,
} from '../../utils/notifications';
import { normalizeCompressorType } from '../../utils/normalizeCompressorType';
import { EMISSIONS_API_URL } from '../../utils/apiConfig';
import { fetchDashboardSnapshot, serializeReportingYear } from '../../utils/dashboardRefresh';

import './DataCollectionWizard.css';

import DataSourcesStep from './steps/DataSourcesStep';
import EnergyHeatingStep from './steps/EnergyHeatingStep';
import OperationStep from './steps/OperationStep';
import OtherStep from './steps/OtherStep';
import PaintRepairStep from './steps/PaintRepairStep';

const BRAND = '#004A53';
const PRIMARY = '#005654';
const INTERACTION = '#026F6D';

const TEXT = '#0f172a';
const MUTED = '#64748b';
const SOFT_BORDER = '#e2e8f0';
const SOFT_SURFACE = '#f8fafc';
const GREEN_SURFACE = '#f0f7f6';
const GREEN_BORDER = '#b7cfcc';

function applyGreenOneSdxButtonStyles(root) {
  if (!root) return undefined;

  function styleSdxButton(button) {
    const shadowRoot =
      button.shadowRoot;

    if (!shadowRoot) {
      return;
    }

    if (
      shadowRoot.querySelector(
        '[data-greenone-button-styles]'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.setAttribute(
      'data-greenone-button-styles',
      'true'
    );

    style.textContent = `
      button.primary {
        background: ${PRIMARY} !important;
        border-color: ${PRIMARY} !important;
        color: #fff !important;
      }

      button.primary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.secondary {
        background: #fff !important;
        border-color: ${INTERACTION} !important;
        color: ${INTERACTION} !important;
      }

      button.secondary:hover {
        background: ${INTERACTION} !important;
        border-color: ${INTERACTION} !important;
        color: #fff !important;
      }

      button.primary:focus-visible,
      button.secondary:focus-visible {
        outline: 3px solid ${PRIMARY} !important;
        outline-offset: 2px !important;
      }
    `;

    shadowRoot.appendChild(
      style
    );
  }

  function applyButtonStyles() {
    root
      .querySelectorAll(
        'sdx-button'
      )
      .forEach(
        styleSdxButton
      );
  }

  applyButtonStyles();

  const observer =
    new MutationObserver(
      applyButtonStyles
    );

  observer.observe(
    root,
    {
      childList: true,
      subtree: true,
    }
  );

  return () => {
    observer.disconnect();
  };
}

const STEPS = [
  {
    label: 'Datenquellen',
    short: 'Daten',
  },
  {
    label: 'Betrieb & Aktivitäten',
    short: 'Betrieb',
  },
  {
    label: 'Energie & Heizung',
    short: 'Energie',
  },
  {
    label: 'Lackieren & Reparieren',
    short: 'Lackieren',
  },
  {
    label: 'Weiteres',
    short: 'Weiteres',
  },
];

const STORAGE_KEY =
  `datenerhebung_completed_years:${EMISSIONS_API_URL}`;

const YEAR_DATA_STORAGE_KEY =
  `datenerhebung_year_data:${EMISSIONS_API_URL}`;

function getCompletedYears() {
  try {
    const years = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ||
        '[]'
    );

    if (!Array.isArray(years)) {
      return [];
    }

    const savedYearData =
      getSavedYearData();

    return years.filter(year =>
      !hasUnsavedChanges(savedYearData[year])
    );
  } catch {
    return [];
  }
}

function hasUnsavedChanges(data) {
  return (
    data?.isChanged === true ||
    data?.isChanged === 'true'
  );
}

function markYearCompleted(year) {
  const years = getCompletedYears();

  if (!years.includes(year)) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([
        ...years,
        year,
      ])
    );
  }
}

function getSavedYearData() {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(
        YEAR_DATA_STORAGE_KEY
      ) || '{}'
    );

    return parsed &&
      typeof parsed === 'object'
      ? parsed
      : {};
  } catch {
    return {};
  }
}

function saveYearData(year, data) {
  const savedData =
    getSavedYearData();

  localStorage.setItem(
    YEAR_DATA_STORAGE_KEY,
    JSON.stringify({
      ...savedData,
      [year]: data,
    })
  );
}

function deleteYearData(year) {
  const savedData =
    getSavedYearData();

  delete savedData[year];

  if (Object.keys(savedData).length === 0) {
    localStorage.removeItem(
      YEAR_DATA_STORAGE_KEY
    );
    return;
  }

  localStorage.setItem(
    YEAR_DATA_STORAGE_KEY,
    JSON.stringify(savedData)
  );
}

function StepTabs({
  currentStep,
  visitedSteps,
  onStepClick,
}) {
  return (
    <div className="data-collection-tabs">
      {STEPS.map(
        (step, index) => {
          const isActive =
            currentStep === index;

          const isDone =
            visitedSteps.has(index) &&
            !isActive;

          const isClickable =
            visitedSteps.has(index) ||
            index <= currentStep;

          return (
            <button
              key={index}
              type="button"
              onClick={() =>
                isClickable &&
                onStepClick(index)
              }
              disabled={!isClickable}
              title={step.label}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                height: 48,
                padding: '6px 8px',
                border: 'none',
                borderBottom:
                  isActive
                    ? `3px solid ${PRIMARY}`
                    : '3px solid transparent',
                background:
                  'transparent',
                color: isActive
                  ? BRAND
                  : isDone
                    ? INTERACTION
                    : '#94a3b8',
                cursor: isClickable
                  ? 'pointer'
                  : 'default',
                fontFamily:
                  'inherit',
                fontSize: 14,
                fontWeight:
                  isActive
                    ? 700
                    : 500,
                display: 'flex',
                alignItems:
                  'center',
                justifyContent:
                  'center',
                gap: 8,
                whiteSpace:
                  'nowrap',
                overflow:
                  'hidden',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius:
                    '50%',
                  flexShrink: 0,
                  display:
                    'inline-flex',
                  alignItems:
                    'center',
                  justifyContent:
                    'center',
                  fontSize: 13,
                  fontWeight: 700,
                  border: `2px solid ${
                    isActive ||
                    isDone
                      ? INTERACTION
                      : '#cbd5e1'
                  }`,
                  background:
                    isActive
                      ? PRIMARY
                      : 'white',
                  color: isActive
                    ? 'white'
                    : isDone
                      ? INTERACTION
                      : '#94a3b8',
                }}
              >
                {isDone
                  ? '✓'
                  : index + 1}
              </span>

              <span
                style={{
                  overflow:
                    'hidden',
                  textOverflow:
                    'ellipsis',
                }}
              >
                {step.short}
              </span>
            </button>
          );
        }
      )}
    </div>
  );
}

function getYearSelectionData(backendYears) {
  const savedYearData = getSavedYearData();
  const confirmedYears = backendYears.map(Number);
  const localCompleted = getCompletedYears()
    .filter(year => savedYearData[year] && confirmedYears.includes(Number(year)))
    .map(Number);
  const localChangedYears = Object.entries(savedYearData)
    .filter(([, data]) => hasUnsavedChanges(data))
    .map(([year]) => Number(year));
  const allKnownYears = [...new Set([
    ...localChangedYears,
    ...confirmedYears,
  ])].filter(year => Number.isInteger(year) && year >= 2020);

  return { localCompleted, localChangedYears, allKnownYears };
}

function YearSelectionScreen({
  onSelectYear,
  onNewYear,
  backendYears = [],
}) {
  const [picking, setPicking] =
    useState(false);

  const [
    pickerYear,
    setPickerYear,
  ] = useState('');

  const { localCompleted, localChangedYears, allKnownYears } =
    getYearSelectionData(backendYears);

  const currentYear =
    new Date().getFullYear();

  const maxKnown =
    allKnownYears.length > 0
      ? Math.max(
          ...allKnownYears
        )
      : null;

  const suggestedYear =
    maxKnown !== null
      ? maxKnown + 1
      : currentYear - 1;

  const sortedKnownYears = [
    ...allKnownYears,
  ].sort(
    (a, b) => b - a
  );

  function openPicker() {
    setPickerYear(
      String(suggestedYear)
    );

    setPicking(true);
  }

  function confirmPicker() {
    const year = parseInt(
      pickerYear,
      10
    );

    if (year < 2020) {
      showAppNotification({ message: 'Das Erhebungsjahr darf nicht vor 2020 liegen.', type: 'warning' });
      return;
    }

    if (
      !year ||
      year < 2020 ||
      year >
        currentYear + 5
    ) {
      return;
    }

    setPicking(false);

    if (
      localCompleted.includes(
        year
      )
    ) {
      onSelectYear(year);
    } else {
      onNewYear(year);
    }
  }

  return (
    <div className="data-collection-year-screen">
      <div
        style={{
          marginBottom: 32,
        }}
      >
        <h1
          style={{
            margin:
              '0 0 8px',
            color: BRAND,
          }}
        >
          Datenerhebung
        </h1>

        <p
          style={{
            margin: 0,
            color: MUTED,
            fontSize: 15,
            lineHeight: 1.55,
            maxWidth: 720,
          }}
        >
          Willkommen in der
          GreenOne Datenerhebung.
          In wenigen Minuten
          erfassen Sie die
          wichtigsten Angaben zu
          Energie, Lack und
          Reparatur in Ihrem
          Betrieb. Daraus entsteht
          die Grundlage, um
          CO₂-Potenziale und
          mögliche
          Kosteneinsparungen
          sichtbar zu machen.
          Bereit? Dann legen Sie
          los mit dem ersten
          Schritt:
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {[...sortedKnownYears]
          .reverse()
          .map(year => {
            const isLocalDone =
              localCompleted.includes(
                year
              );

            const isBackendOnly =
              !isLocalDone &&
              backendYears
                .map(Number)
                .includes(year);
            const isLocalChanged =
              localChangedYears.includes(year);

            return (
              <button
                key={year}
                type="button"
                onClick={() =>
                  onSelectYear(
                    year
                  )
                }
                style={{
                  background:
                    isLocalChanged
                      ? '#fffbeb'
                      : GREEN_SURFACE,
                  border: `1.5px solid ${
                    isLocalChanged
                      ? '#f59e0b'
                      : PRIMARY
                  }`,
                  borderRadius: 10,
                  padding:
                    '14px 20px',
                  cursor:
                    'pointer',
                  fontFamily:
                    'inherit',
                  display: 'flex',
                  flexDirection:
                    'column',
                  alignItems:
                    'flex-start',
                  gap: 4,
                  minWidth: 120,
                }}
              >
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color:
                      isLocalChanged
                        ? '#92400e'
                        : BRAND,
                  }}
                >
                  {year}
                </span>

                {(isLocalDone ||
                  isBackendOnly ||
                  isLocalChanged) && (
                  <span
                    style={{
                      fontSize: 12,
                      color: isLocalChanged
                        ? '#b45309'
                        : INTERACTION,
                      fontWeight: 600,
                    }}
                  >
                    {isLocalChanged
                      ? 'Entwurf fortsetzen'
                      : '✓ Bearbeiten'}
                  </span>
                )}
              </button>
            );
          })}

        {!picking ? (
          <button
            type="button"
            onClick={openPicker}
            style={{
              background: 'white',
              border: `2px dashed ${INTERACTION}`,
              borderRadius: 10,
              padding:
                '14px 20px',
              cursor: 'pointer',
              fontFamily:
                'inherit',
              display: 'flex',
              flexDirection:
                'column',
              alignItems:
                'flex-start',
              gap: 4,
              minWidth: 120,
            }}
          >
            <span
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: INTERACTION,
              }}
            >
              ＋
            </span>

            <span
              style={{
                fontSize: 12,
                color: MUTED,
              }}
            >
              Neu erfassen
            </span>
          </button>
        ) : (
          <div
            style={{
              background: 'white',
              border: `2px solid ${INTERACTION}`,
              borderRadius: 12,
              padding: '1.5rem',
              width: '100%',
              boxSizing:
                'border-box',
            }}
          >
            <p
              style={{
                margin:
                  '0 0 12px',
                fontSize: 14,
                fontWeight: 600,
                color: TEXT,
              }}
            >
              Für welches Jahr
              möchten Sie Daten
              erfassen?
            </p>

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems:
                  'center',
                flexWrap: 'wrap',
              }}
            >
              <input
                type="number"
                value={pickerYear}
                onChange={event =>
                  setPickerYear(
                    event.target
                      .value
                  )
                }
                onKeyDown={event =>
                  event.key ===
                    'Enter' &&
                  confirmPicker()
                }
                min={2020}
                max={
                  currentYear + 5
                }
                style={{
                  padding:
                    '10px 14px',
                  border: `1.5px solid ${INTERACTION}`,
                  borderRadius: 8,
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily:
                    'inherit',
                  width: 110,
                  textAlign:
                    'center',
                }}
                autoFocus
              />

              <button
                type="button"
                onClick={
                  confirmPicker
                }
                style={{
                  background: PRIMARY,
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding:
                    '10px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily:
                    'inherit',
                }}
              >
                Bestätigen
              </button>

              <button
                type="button"
                onClick={() =>
                  setPicking(false)
                }
                style={{
                  background:
                    '#f1f5f9',
                  color: MUTED,
                  border: 'none',
                  borderRadius: 8,
                  padding:
                    '10px 16px',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily:
                    'inherit',
                }}
              >
                Abbrechen
              </button>
            </div>

            <p
              style={{
                margin:
                  '10px 0 0',
                fontSize: 12,
                color: '#94a3b8',
              }}
            >
              Vorschlag:{' '}
              {suggestedYear} — Sie
              können das Jahr
              anpassen, falls nötig.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DataCollectionWizard() {
  const [validatedSteps, setValidatedSteps] = useState(() => new Set());

  const [screen, setScreen] =
    useState('years');

  const [
    formData,
    setFormDataState,
  ] = useState(
    getInitialFormData()
  );

  const [
    currentStep,
    setCurrentStep,
  ] = useState(0);

  const [
    visitedSteps,
    setVisitedSteps,
  ] = useState(
    new Set([0])
  );

  const [
    submitStatus,
    setSubmitStatus,
  ] = useState(null);

  const [
    isReadOnly,
    setIsReadOnly,
  ] = useState(false);

  const [
    backendYears,
    setBackendYears,
  ] = useState([]);

  const [
    currentUserId,
    setCurrentUserId,
  ] = useState('');

  const [
    currentCompanyName,
    setCurrentCompanyName,
  ] = useState('');

  const [
    showOverwriteConfirm,
    setShowOverwriteConfirm,
  ] = useState(false);

  const [
    fleetUploadFile,
    setFleetUploadFile,
  ] = useState(null);

  useEffect(() => {
    if (
      screen !== 'form'
    ) {
      return undefined;
    }

    const page =
      document.querySelector(
        '.data-collection-page'
      );

    if (!page) {
      return undefined;
    }

    return applyGreenOneSdxButtonStyles(
      page
    );
  }, [screen]);

  useEffect(() => {
    async function fetchCurrentUserId() {
      try {
        const user =
          await getCurrentUser();

        setCurrentUserId(
          user.userId ||
            user.username ||
            ''
        );
      } catch {
        setCurrentUserId('');
      }
    }

    async function fetchCurrentCompanyName() {
      try {
        const attributes =
          await fetchUserAttributes();

        setCurrentCompanyName(
          attributes?.[
            'custom:company'
          ] || ''
        );
      } catch {
        setCurrentCompanyName('');
      }
    }

    fetchCurrentUserId();
    fetchCurrentCompanyName();
  }, []);

  useEffect(() => {
    const companyName =
      currentCompanyName?.trim();

    if (!companyName) {
      setBackendYears([]);
      return undefined;
    }

    async function fetchBackendYears() {
      try {
        const session =
          await fetchAuthSession();

        const token =
          session?.tokens?.idToken?.toString();

        const response =
          await fetch(
            EMISSIONS_API_URL,
            {
              headers: {
                ...(token
                  ? {
                      Authorization: `Bearer ${token}`,
                    }
                  : {}),
                'Content-Type':
                  'application/json',
              },
            }
          );

        if (!response.ok) {
          return;
        }

        const payload =
          await response.json();

        let parsed = payload;

        if (
          typeof payload?.body ===
          'string'
        ) {
          try {
            parsed = JSON.parse(
              payload.body
            );
          } catch {
            parsed = payload.body;
          }
        } else if (
          payload?.body &&
          typeof payload.body ===
            'object'
        ) {
          parsed = payload.body;
        }

        const rows =
          Array.isArray(parsed)
            ? parsed
            : Array.isArray(
                  parsed?.reporting_data
                )
              ? parsed.reporting_data
              : Array.isArray(
                    parsed?.rows
                  )
                ? parsed.rows
                : [];

        const years = [
          ...new Set(
            rows
              .filter(
                row =>
                  row?.year != null
              )
              .map(row =>
                Number(row.year)
              )
              .filter(
                year =>
                  Number.isFinite(
                    year
                  )
              )
          ),
        ];

        setBackendYears(years);
      } catch {
        // Backend nicht erreichbar.
      }
    }

    fetchBackendYears();
  }, [currentCompanyName]);

  useEffect(() => {
    if (
      screen !== 'form'
    ) {
      return;
    }

    window.requestAnimationFrame(
      () => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior:
            'smooth',
        });
      }
    );
  }, [
    currentStep,
    screen,
  ]);

  const operationType =
    deriveOperationType(
      formData.activitySpenglerarbeiten,
      formData.activityLackierarbeiten,
      formData.activityWeitere
    );

  useEffect(() => {
    if (
      !visitedSteps.has(3)
    ) {
      return;
    }

    setFormData(previous => ({
      ...previous,
      materials: {
        ...MATERIAL_DEFAULTS[
          operationType
        ],
      },
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationType]);

  function setFormData(updater) {
    setFormDataState(previous => {
      const nextFormData =
        typeof updater === 'function'
          ? updater(previous)
          : updater;

      return {
        ...nextFormData,
        isChanged: true,
      };
    });
  }

  function setField(
    key,
    value
  ) {
    setFormData(previous => ({
      ...previous,
      [key]: value,
    }));

  }

  function goToStep(step) {
    if (step > currentStep && !isReadOnly && !validateCurrentStep()) return;
    setCurrentStep(step);

    setVisitedSteps(
      previous =>
        new Set([
          ...previous,
          step,
        ])
    );
  }

  function validateCurrentStep() {
    const result = validateForm(formData);
    const errors = result.errors.filter(error => error.step === currentStep);
    setValidatedSteps(previous => new Set([...previous, currentStep]));
    [...errors, ...result.warnings.filter(warning => warning.step === currentStep)]
      .forEach(({ message }) => showAppNotification({ message, type: 'warning' }));
    setSubmitStatus(errors.length ? 'error' : null);
    if (errors.length) window.scrollTo({ top: 0, behavior: 'smooth' });
    return errors.length === 0;
  }

  function goNext() {
    if (currentStep < STEPS.length - 1) goToStep(currentStep + 1);
  }

  function goPrev() {
    if (currentStep > 0) {
      setCurrentStep(
        currentStep - 1
      );
    }
  }

useEffect(() => {
  if (
    screen === 'form' &&
    formData.reportingYear &&
    formData.isChanged
  ) {
    saveYearData(formData.reportingYear, formData);
  }


}, [screen, formData]);

  async function openYear(
    year,
    readOnly = false
  ) {
    if (!Number.isInteger(Number(year)) || Number(year) < 2020) return;

    const companyName =
      currentCompanyName?.trim();

    if (!year || !companyName) {
      showAppNotification({
        message:
          'Unternehmen noch nicht geladen. Bitte kurz warten und erneut versuchen.',
        type: 'warning',
      });
      return;
    }

    const localYearData =
      getSavedYearData()[year] ||
      null;
    const localHasChanges =
      hasUnsavedChanges(localYearData);
    let uploadedYearData =
      localYearData;

    try {

      if (
        backendYears
          .map(Number)
          .includes(Number(year)) &&
        !localHasChanges
      ) {
        try {
          const file_path = await getCsvS3Filepath(companyName, year)
          console.log('Download Filepath is ' + file_path)
          uploadedYearData =
            await loadDataCollectionCsvfromS3(file_path);
        } catch (error) {
          if (!localYearData) {
            throw error;
          }
          showAppNotification({
            message: `Serverdaten für ${year} konnten nicht geladen werden. Es werden lokal gespeicherte Daten angezeigt.`,
            type: 'warning',
          });
        }
      }

      setFormDataState({
        ...getInitialFormData(),
        ...(uploadedYearData || {}),
        compressorType: normalizeCompressorType(uploadedYearData?.compressorType ?? ''),
        gcsRequested: false,
        silverdatRequested:
          false,
        reportingYear: year,
        isChanged: localHasChanges,
      });

      setFleetUploadFile(null);
      setValidatedSteps(new Set());
      setIsReadOnly(
        readOnly && !localHasChanges
      );

      setCurrentStep(0);
      setVisitedSteps(
        new Set([0])
      );
      setSubmitStatus(null);
      setScreen('form');
    } catch (error) {
      console.error(
        'openYear failed:',
        error
      );

      showAppNotification({
        message:
          error instanceof TypeError
            ? `Daten für ${year} konnten nicht vom Server geladen werden. Bitte später erneut versuchen.`
            : error instanceof Error
            ? error.message
            : 'Daten für das ausgewählte Jahr konnten nicht geladen werden.',
        type: 'error',
      });
    }
  }

  async function getCsvS3Filepath(company, reportingYear){
    if (!company || !reportingYear) {
      throw new Error(
        'Es wird keine Firma oder Jahr mitgegeben'
      );
    }
    // Fetch Filepath from newest CSV
    const query = new URLSearchParams({
      folder: 'bronze/datenerhebung/',
      company: `${company}`,
      reportingYear: `${reportingYear}`
    });

    const filesResult = await fetch(
      `https://a2kas5vv3sqmspd2nluzpqrzje0kirwh.lambda-url.eu-west-1.on.aws/?${query.toString()}`
    );

    if (filesResult.status === 404) {
      return null;
    }

    if (!filesResult.ok) {
      throw new Error(
        `CSV-Dateien konnten nicht geladen werden (${filesResult.status})`
      );
    }

    const responsePath = await filesResult.json();
    const filePath =
      typeof responsePath === 'string'
        ? responsePath
        : responsePath?.file_path;

    return typeof filePath === 'string' && filePath.trim()
      ? filePath.trim()
      : null;
  }

  async function deleteCsvfromS3(file_path){
    if (!file_path) {
      throw new Error(
        'Es wird kein AWS Datei Pfad mitgegeben'
      );
    }
    console.log(file_path)


    // Generate Presigned URL for this file
    const res = await fetch(
      `https://bfozwlwiw52n6r2u3x7wedmpmi0aksfw.lambda-url.eu-west-1.on.aws/?file_path=${encodeURIComponent(
        file_path
      )}`
    );

    if (!res.ok) {
      throw new Error(`Delete-URL konnte nicht geladen werden (${res.status})`);
    }

    const { deleteUrl } = await res.json();

    if (!deleteUrl) {
      throw new Error('Delete-URL fehlt in der Serverantwort');
    }



    // Delete file
   const deleteRes = await fetch(deleteUrl, {
    method: "DELETE"
  });

    if (!deleteRes.ok) {
      throw new Error(
        `CSV-Delete fehlgeschlagen (${deleteRes.status})`
      );
    }
  }


  async function loadDataCollectionCsvfromS3(file_path) {
    if (!file_path) {
      throw new Error(
        'Es wird kein AWS Datei Pfad mitgegeben'
      );
    }
    console.log(file_path)

    // Generate Presigned URL for this file
    const res = await fetch(
      `https://kga24fs6nqwbatbghy5d7ka7iq0wgypg.lambda-url.eu-west-1.on.aws/?file_path=${encodeURIComponent(
        file_path
      )}`
    );

    if (!res.ok) {
      throw new Error(`Download-URL konnte nicht geladen werden (${res.status})`);
    }

    const { downloadUrl } = await res.json();

    if (!downloadUrl) {
      throw new Error('Download-URL fehlt in der Serverantwort');
    }



    // Download CSV file
    const csvResponse = await fetch(downloadUrl);

    if (!csvResponse.ok) {
      throw new Error(`Datei konnte nicht geladen werden (${csvResponse.status})`);
    }

    const csvText = await csvResponse.text();



    // Convert the CSV File to JSON
    const formData = parseDataCollectionCsv(csvText);
    return formData
  }




  async function uploadDataCollectionCsvToS3(csv, reportingYear) {
    const BOM = '﻿';
    const blob = new Blob(
      [BOM, csv],
      { type: 'text/csv;charset=utf-8;' }
    );

    const timestamp =
      new Date()
        .toISOString()
        .replace(/[:.]/g, '-');

    const filename = `datenerhebung_${
      reportingYear ||
      new Date().getFullYear()
    }_${
      currentCompanyName ||
      'unbekannt'
    }_${timestamp}.csv`;

    const res = await fetch(
      `https://3h7d6zdnlo6xp4l2shhw4zx6540eaeoo.lambda-url.eu-west-1.on.aws/?filename=${encodeURIComponent(
        filename
      )}&folder=datenerhebung`
    );

    if (!res.ok) {
      throw new Error(
        `Signierte Upload-URL konnte nicht geladen werden (${res.status})`
      );
    }

    const { uploadUrl } =
      await res.json();

    if (!uploadUrl) {
      throw new Error(
        'Upload-URL fehlt in der Serverantwort'
      );
    }

    const uploadRes = await fetch(
      uploadUrl,
      {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': 'text/csv',
        },
      }
    );

    if (!uploadRes.ok) {
      throw new Error(
        `CSV-Upload fehlgeschlagen (${uploadRes.status})`
      );
    }
  }

  async function uploadFleetExcelToS3(file, reportingYear) {
    if (!file) {
      return;
    }

    const timestamp =
      new Date()
        .toISOString()
        .replace(/[:.]/g, '-');

    const filename = `fahrzeugflotte_${
      reportingYear ||
      new Date().getFullYear()
    }_${
      currentCompanyName ||
      'unbekannt'
    }_${timestamp}_${file.name}`;

    const res = await fetch(
      `https://3h7d6zdnlo6xp4l2shhw4zx6540eaeoo.lambda-url.eu-west-1.on.aws/?filename=${encodeURIComponent(
        filename
      )}&folder=fahrzeugflotte`
    );

    if (!res.ok) {
      throw new Error(
        `Signierte Upload-URL für Fahrzeugflotte konnte nicht geladen werden (${res.status})`
      );
    }

    const { uploadUrl } =
      await res.json();

    if (!uploadUrl) {
      throw new Error(
        'Upload-URL für Fahrzeugflotte fehlt in der Serverantwort'
      );
    }

    const uploadRes = await fetch(
      uploadUrl,
      {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type':
            file.name.toLowerCase().endsWith('.xls')
              ? 'application/vnd.ms-excel'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }
    );

    if (!uploadRes.ok) {
      throw new Error(
        `Fahrzeugflotten-Upload fehlgeschlagen (${uploadRes.status})`
      );
    }
  }

  async function handleFinish() {
    const result =
      validateForm(
        formData
      );

    setValidatedSteps(new Set(STEPS.map((_, index) => index)));

    if (!result.isValid) {
      result.errors.forEach(
        error => {
          showAppNotification({
            message:
              error.message,
            type: 'warning',
          });
        }
      );

      const firstErrorStep =
        result.errors[0]
          ?.step ?? 0;

      setCurrentStep(
        firstErrorStep
      );

      setVisitedSteps(
        previous =>
          new Set([
            ...previous,
            firstErrorStep,
          ])
      );

      setSubmitStatus(
        'error'
      );

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      return;
    }

    if (result.warnings.length && !window.confirm(
      `${result.warnings.map(warning => warning.message).join('\n\n')}\n\nAngaben geprüft und trotzdem abschliessen?`
    )) {
      return;
    }

    const reportingYear = Number(
      formData.reportingYear
    );

    const yearAlreadyHasData =
      getCompletedYears()
        .map(Number)
        .includes(reportingYear) ||
      backendYears
        .map(Number)
        .includes(reportingYear);

    if (yearAlreadyHasData) {
      setShowOverwriteConfirm(
        true
      );
      return;
    }

    await performSave(result);
  }

  async function performSave(
    result
  ) {
    result.warnings.forEach(
      warning => {
        showAppNotification({
          message:
            warning.message,
          type: 'warning',
        });
      }
    );

    // weill be removed as soon crosssync with backend is available
    saveYearData(
      formData.reportingYear,
      formData
    );

    const draft =
      saveProfileDraft(
        formData,
        {
          userId:
            currentUserId,
          companyName:
            currentCompanyName,
        }
      );

    try {
        let filePath = null;
        const previousYearData = serializeReportingYear(
          await fetchDashboardSnapshot(), formData.reportingYear
        );

        try {
          filePath = await getCsvS3Filepath(
            currentCompanyName,
            formData.reportingYear
          );
        } catch (lookupError) {
          console.warn(
            'Existing CSV path lookup failed; saving without deleting an old file.',
            lookupError
          );
        }

      await uploadDataCollectionCsvToS3(
        draft.csv,
        formData.reportingYear
      );

      if (
        fleetUploadFile &&
        formData.fleetImportSuccessful ===
          true
      ) {
        await uploadFleetExcelToS3(
          fleetUploadFile,
          formData.reportingYear
        );
      }

      if (filePath) {
        await deleteCsvfromS3(filePath);
      }

      const savedFormData = {
        ...formData,
        isChanged: false,
      };
      markYearCompleted(
        formData.reportingYear
      );
      deleteYearData(
        formData.reportingYear
      );
      setFormDataState(savedFormData);

      setSubmitStatus(
        'success'
      );

      sessionStorage.setItem(
        'datenerhebung_saved',
        JSON.stringify({ year: formData.reportingYear, previousYearData })
      );

      window.location.href =
        '/dashboard';
    } catch (error) {
      console.error(
        'uploadDataCollectionCsvToS3 failed:',
        error
      );

      showAppNotification({
        message:
          error instanceof Error
            ? `Speichern in S3 fehlgeschlagen, Daten sind temporär alternativ im Zwischenspeicher gespeichert: ${error.message}`
            : 'Speichern in S3 fehlgeschlagen, Daten sind temporär alternativ im Zwischenspeicher gespeichert:',
        type: 'error',
      });

      setSubmitStatus(
        'error'
      );
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  async function confirmOverwriteAndSave() {
    setShowOverwriteConfirm(
      false
    );

    if (!hasUnsavedChanges(formData) && !fleetUploadFile) {
      // No upload means there is no new dashboard data to wait for.
      window.location.href = '/dashboard';
      return;
    }

    const result =
      validateForm(
        formData
      );

    if (!result.isValid) {
      await handleFinish();
      return;
    }
    await performSave(result);
  }

  if (
    screen === 'years'
  ) {
    return (
      <YearSelectionScreen
        onSelectYear={year =>
          openYear(
            year,
            false
          )
        }
        onNewYear={year =>
          openYear(
            year,
            false
          )
        }
        backendYears={
          backendYears
        }
      />
    );
  }

  const fieldErrors = Object.fromEntries(
    validateForm(formData).errors
      .filter(error => validatedSteps.has(error.step))
      .map(error => [error.field, error.message])
  );

  const { localCompleted: completedYears, allKnownYears } =
    getYearSelectionData(backendYears);

  const headerYears = [
    ...new Set([
      ...allKnownYears,
      Number(formData.reportingYear),
    ]),
  ].sort(
    (a, b) => b - a
  );

  return (
    <div className="sdx data-collection-page">
      <div className="data-collection-header">
        <div className="data-collection-header-main">
          <sdx-button
            theme="secondary"
            variant="ghost"
            label="← Übersicht"
            onClick={() =>
              setScreen('years')
            }
          ></sdx-button>

          <h1 className="data-collection-header-title">
            Datenerhebung
          </h1>

          <span
            style={{
              fontSize: 13,
              padding:
                '2px 10px',
              borderRadius: 999,
              background:
                GREEN_SURFACE,
              color:
                BRAND,
              fontWeight: 600,
            }}
          >
            {
              formData.reportingYear
            }
          </span>
        </div>

        {headerYears.length >
          0 && (
          <div className="data-collection-year-navigation">
            <span
              style={{
                fontSize: 14,
                color:
                  MUTED,
                fontWeight: 500,
              }}
            >
              Jahr:
            </span>

            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap:
                  'wrap',
              }}
            >
              {[...headerYears].reverse().map(
                year => {
                  const isActive =
                    Number(formData.reportingYear) ===
                    year;

                  const isDone =
                    completedYears.includes(
                      year
                    );

                  return (
                    <button
                      key={year}
                      type="button"
                      onClick={() =>
                        openYear(
                          year,
                          false
                        )
                      }
                      style={{
                        background:
                          isActive
                            ? isDone
                              ? GREEN_SURFACE
                              : '#fffbeb'
                            : SOFT_SURFACE,
                        border:
                          isActive
                            ? `1.5px solid ${
                                isDone
                                  ? PRIMARY
                                  : '#f59e0b'
                              }`
                            : `1px solid ${SOFT_BORDER}`,
                        borderRadius: 8,
                        padding:
                          '6px 14px',
                        cursor:
                          'pointer',
                        fontSize: 14,
                        fontWeight:
                          isActive
                            ? 700
                            : 400,
                        color:
                          isActive
                            ? isDone
                              ? BRAND
                              : '#92400e'
                            : MUTED,
                        fontFamily:
                          'inherit',
                      }}
                    >
                      {year}
                    </button>
                  );
                }
              )}
            </div>
          </div>
        )}
      </div>

      {isReadOnly && (
        <div
          style={{
            background:
              '#fffbeb',
            border:
              '1px solid #fde68a',
            borderRadius: 10,
            padding:
              '12px 16px',
            marginBottom: 14,
            display: 'flex',
            alignItems:
              'center',
            gap: 10,
          }}
        >
          <span
            style={{
              fontSize: 18,
            }}
          >
            🔒
          </span>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              color:
                '#92400e',
              fontWeight: 500,
            }}
          >
            Ansicht{' '}
            {
              formData.reportingYear
            }{' '}
            — abgeschlossenes
            Reportingjahr,
            schreibgeschützt.
          </p>
        </div>
      )}

      {submitStatus ===
        'success' && (
        <div
          style={{
            background:
              GREEN_SURFACE,
            border:
              `1px solid ${GREEN_BORDER}`,
            borderRadius: 10,
            padding:
              '1rem 1.25rem',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems:
                'flex-start',
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 22,
              }}
            >
              ✅
            </span>

            <div>
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color:
                    BRAND,
                }}
              >
                Daten
                gespeichert.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="data-collection-mobile-step">
        <div className="data-collection-mobile-step-top">
          <span className="data-collection-mobile-step-label">
            {
              STEPS[
                currentStep
              ].label
            }
          </span>

          <span className="data-collection-mobile-step-count">
            Schritt{' '}
            {currentStep + 1}{' '}
            von {STEPS.length}
          </span>
        </div>

        <div className="data-collection-mobile-progress">
          <div
            className="data-collection-mobile-progress-value"
            style={{
              width: `${
                ((currentStep +
                  1) /
                  STEPS.length) *
                100
              }%`,
            }}
          />
        </div>
      </div>

      <StepTabs
        currentStep={
          currentStep
        }
        visitedSteps={
          visitedSteps
        }
        onStepClick={
          goToStep
        }
      />

      {currentStep === 0 && (
        <DataSourcesStep
          formData={formData}
          setField={setField}
          setFormData={setFormData}
          isReadOnly={
            isReadOnly
          }
        />
      )}

      {currentStep === 1 && (
        <OperationStep
          fieldErrors={fieldErrors}
          formData={formData}
          setField={setField}
          isReadOnly={
            isReadOnly
          }
        />
      )}

      {currentStep === 2 && (
        <EnergyHeatingStep
          fieldErrors={fieldErrors}
          formData={formData}
          setField={setField}
          setFormData={setFormData}
          isReadOnly={
            isReadOnly
          }
        />
      )}

      {currentStep === 3 && (
        <PaintRepairStep
          fieldErrors={fieldErrors}
          formData={formData}
          setField={setField}
          setFormData={
            setFormData
          }
          isReadOnly={
            isReadOnly
          }
          operationType={
            operationType
          }
        />
      )}

      {currentStep === 4 && (
        <OtherStep
          fieldErrors={fieldErrors}
          formData={formData}
          setField={setField}
          isReadOnly={
            isReadOnly
          }
          onFleetFileChange={
            setFleetUploadFile
          }
        />
      )}

      <div className="data-collection-navigation">
        <sdx-button
          theme="secondary"
          label="Zurück"
          onClick={goPrev}
          disabled={
            currentStep === 0 ||
            undefined
          }
        ></sdx-button>

        <div className="data-collection-progress-dots">
          {STEPS.map(
            (_, index) => (
              <div
                key={index}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius:
                    '50%',
                  background:
                    index === currentStep
                      ? INTERACTION
                      : SOFT_BORDER,
                  transition:
                    'background 0.2s',
                }}
              />
            )
          )}
        </div>

        {currentStep <
        STEPS.length - 1 ? (
          <sdx-button
            variant="primary"
            label="Weiter"
            onClick={goNext}
          ></sdx-button>
        ) : (
          <sdx-button
            variant="primary"
            label="Profil abschliessen"
            onClick={
              handleFinish
            }
            disabled={
              isReadOnly ||
              undefined
            }
          ></sdx-button>
        )}
      </div>

      {showOverwriteConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 74, 83, 0.45)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.16)',
              padding: '1.75rem',
            }}
          >
            <h3
              style={{
                margin: '0 0 0.75rem',
                color: BRAND,
              }}
            >
              Daten für {formData.reportingYear} überschreiben?
            </h3>

            <p
              style={{
                margin: '0 0 1.5rem',
                color: MUTED,
              }}
            >
              Für dieses Jahr sind bereits Daten vorhanden. Wenn du fortfährst, werden die bestehenden Daten für {formData.reportingYear} überschrieben.
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem',
              }}
            >
              <sdx-button
                theme="secondary"
                label="Abbrechen"
                onClick={() =>
                  setShowOverwriteConfirm(false)
                }
              ></sdx-button>

              <sdx-button
                variant="primary"
                label="Überschreiben & Speichern"
                onClick={confirmOverwriteAndSave}
              ></sdx-button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
